/* ================================================= */
/* PASTORAL STATE COMPRESSION LAYER                  */
/* Canonical semantic consolidation of all engines   */
/*                                                   */
/* Inputs:  intelligence, memory, workflows,          */
/*          scheduling, narrative, execution         */
/*                                                   */
/* Output:  ONE stable, serializable state object    */
/*          ready for: assistant, notifications,     */
/*          weekly planning, persistence, scheduling */
/*                                                   */
/* Rules:                                            */
/* - No UI. No Tailwind. No React. No JSX.           */
/* - 100% deterministic. No side effects.            */
/* - No network calls. No mutations.                 */
/* - Serializable: JSON-safe (no Set, no Function)  */
/* - No semantic duplication across fields           */
/* ================================================= */

import { NARRATIVE_THEME } from "./pastoral-narrative";
import { EXECUTION_STATE }  from "./pastoral-execution";
import { TREND_TYPES }      from "./pastoral-memory";
import {
  WORKFLOW_STAGES,
  WORKFLOW_URGENCY,
} from "./pastoral-workflows";
import { RISK_LEVEL } from "./pastoral-intelligence";
import { LOAD_LEVEL } from "./pastoral-scheduling";
import { ATTENTION_LEVELS } from "./pastoral-attention";
import { getLocalDateString } from "./date";

/* ============================= */
/* CANONICAL STATE TYPES         */
/* ============================= */

export const URGENCY_LEVEL = {
  IMMEDIATE: "immediate",
  SOON:      "soon",
  MONITOR:   "monitor",
  NONE:      "none",
};

export const CONTINUITY_STATE = {
  ACTIVE:     "active",
  RECOVERING: "recovering",
  DECLINING:  "declining",
  NEGLECTED:  "neglected",
  UNKNOWN:    "unknown",
};

/* ============================= */
/* STATIC THEME → URGENCY MAP    */
/* ============================= */

const THEME_URGENCY = {
  [NARRATIVE_THEME.CRISIS_CLUSTER]:       URGENCY_LEVEL.IMMEDIATE,
  [NARRATIVE_THEME.CONTINUITY_BREAKDOWN]: URGENCY_LEVEL.SOON,
  [NARRATIVE_THEME.SPIRITUAL_FATIGUE]:    URGENCY_LEVEL.SOON,
  [NARRATIVE_THEME.PRAYER_BURDEN]:        URGENCY_LEVEL.SOON,
  [NARRATIVE_THEME.FOLLOWUP_REQUIRED]:    URGENCY_LEVEL.MONITOR,
  [NARRATIVE_THEME.PASTORAL_OVERLOAD]:    URGENCY_LEVEL.SOON,
  [NARRATIVE_THEME.RECOVERY_MOMENTUM]:    URGENCY_LEVEL.MONITOR,
  [NARRATIVE_THEME.GROWTH_WINDOW]:        URGENCY_LEVEL.MONITOR,
  [NARRATIVE_THEME.HEALTHY_STABILITY]:    URGENCY_LEVEL.NONE,
};

/* ============================= */
/* STATIC THEME → RISK MAP       */
/* Fallback when risk not passed */
/* ============================= */

const THEME_TO_RISK = {
  [NARRATIVE_THEME.CRISIS_CLUSTER]:       RISK_LEVEL.critical,
  [NARRATIVE_THEME.CONTINUITY_BREAKDOWN]: RISK_LEVEL.high,
  [NARRATIVE_THEME.SPIRITUAL_FATIGUE]:    RISK_LEVEL.high,
  [NARRATIVE_THEME.PRAYER_BURDEN]:        RISK_LEVEL.high,
  [NARRATIVE_THEME.FOLLOWUP_REQUIRED]:    RISK_LEVEL.medium,
  [NARRATIVE_THEME.PASTORAL_OVERLOAD]:    RISK_LEVEL.high,
  [NARRATIVE_THEME.RECOVERY_MOMENTUM]:    RISK_LEVEL.medium,
  [NARRATIVE_THEME.GROWTH_WINDOW]:        RISK_LEVEL.medium,
  [NARRATIVE_THEME.HEALTHY_STABILITY]:    RISK_LEVEL.healthy,
};

/* ============================= */
/* STATIC ASSISTANT SUMMARIES    */
/* Indexed by theme × continuity */
/* Short, pastoral, no corporate */
/* language                      */
/* ============================= */

const ASSISTANT_SUMMARIES = {
  [NARRATIVE_THEME.CRISIS_CLUSTER]: {
    _default:   "La situación pastoral presenta múltiples señales críticas simultáneas que requieren atención inmediata.",
    recovering: "Hay una visita reciente, pero el patrón de crisis pastoral — oración urgente y ausencia prolongada — persiste.",
    neglected:  "La continuidad pastoral se perdió en un momento de alta presión espiritual y de oración.",
  },
  [NARRATIVE_THEME.CONTINUITY_BREAKDOWN]: {
    _default:   "La continuidad pastoral se ha interrumpido — el acompañamiento necesita retomarse.",
    recovering: "Se retomó el contacto recientemente, pero el patrón de ausencia prolongada aún es visible.",
    active:     "Hay contacto reciente, pero el historial muestra una ruptura de continuidad previa.",
    neglected:  "El acompañamiento no ha sido sostenido — hay un patrón de ausencia extendida.",
  },
  [NARRATIVE_THEME.RECOVERY_MOMENTUM]: {
    _default:   "La relación pastoral está en recuperación — el momento actual es clave para consolidar el ritmo.",
    active:     "El seguimiento se está regularizando después de una etapa de ausencia.",
    declining:  "La recuperación muestra señales de debilitamiento — se recomienda mantener el contacto pronto.",
  },
  [NARRATIVE_THEME.SPIRITUAL_FATIGUE]: {
    _default:   "El estado espiritual requiere atención pastoral — el acompañamiento espiritual debe profundizarse.",
    neglected:  "La ausencia pastoral coincide con un estado espiritual bajo — situación que merece prioridad.",
    active:     "Hay visitas recientes, pero el estado espiritual bajo indica que el acompañamiento debe profundizarse.",
  },
  [NARRATIVE_THEME.PRAYER_BURDEN]: {
    _default:   "Hay oraciones urgentes activas que requieren seguimiento pastoral sostenido.",
    neglected:  "Las oraciones urgentes coinciden con una ausencia de visitas — la carga espiritual necesita atención directa.",
  },
  [NARRATIVE_THEME.FOLLOWUP_REQUIRED]: {
    _default:   "El seguimiento pastoral está pendiente — la visita más reciente fue hace más de un mes.",
    declining:  "El ritmo de visitas está disminuyendo — conviene retomar el contacto pronto.",
    fragmented: "El historial de visitas muestra irregularidad — se recomienda establecer un ritmo más constante.",
  },
  [NARRATIVE_THEME.PASTORAL_OVERLOAD]: {
    _default:   "La carga pastoral distrital supera la capacidad operativa actual.",
  },
  [NARRATIVE_THEME.GROWTH_WINDOW]: {
    _default:   "Hay una oportunidad de acompañamiento espiritual — el proceso bautismal está en curso.",
    recovering: "El proceso bautismal avanza junto con una recuperación del ritmo de visitas — momento favorable para profundizar.",
  },
  [NARRATIVE_THEME.HEALTHY_STABILITY]: {
    _default: null,
  },
};

/* ============================= */
/* DISTRICT ASSISTANT SUMMARIES  */
/* ============================= */

const DISTRICT_SUMMARIES = {
  [NARRATIVE_THEME.HEALTHY_STABILITY]:    null,
  [NARRATIVE_THEME.PASTORAL_OVERLOAD]:    "El distrito muestra señales de sobrecarga pastoral — el volumen de atención supera la capacidad actual.",
  [NARRATIVE_THEME.CRISIS_CLUSTER]:       "El distrito enfrenta múltiples situaciones críticas simultáneas que requieren priorización inmediata.",
  [NARRATIVE_THEME.CONTINUITY_BREAKDOWN]: "El seguimiento pastoral distrital muestra signos de interrupción en varias iglesias.",
  [NARRATIVE_THEME.RECOVERY_MOMENTUM]:    "El distrito está en proceso de recuperación pastoral — varias iglesias retoman su seguimiento.",
  [NARRATIVE_THEME.FOLLOWUP_REQUIRED]:    "Varias iglesias del distrito requieren seguimiento pastoral en los próximos días.",
};

/* ============================= */
/* HELPERS                       */
/* ============================= */

function deriveContinuityState(memory, execution) {
  const trend   = memory?.momentum?.trend    ?? TREND_TYPES.UNKNOWN;
  const exState = execution?.state           ?? EXECUTION_STATE.NOT_STARTED;

  if (
    trend === TREND_TYPES.STABLE_HEALTHY ||
    exState === EXECUTION_STATE.IN_PROGRESS ||
    exState === EXECUTION_STATE.FOLLOWING_THROUGH ||
    exState === EXECUTION_STATE.SUSTAINED
  ) {
    return CONTINUITY_STATE.ACTIVE;
  }

  if (trend === TREND_TYPES.RECOVERING || exState === EXECUTION_STATE.RECOVERING) {
    return CONTINUITY_STATE.RECOVERING;
  }

  if (trend === TREND_TYPES.DECLINING || exState === EXECUTION_STATE.FRAGMENTED) {
    return CONTINUITY_STATE.DECLINING;
  }

  if (
    trend === TREND_TYPES.STABLE_NEGLECTED ||
    exState === EXECUTION_STATE.STALLED ||
    exState === EXECUTION_STATE.OVERDUE
  ) {
    return CONTINUITY_STATE.NEGLECTED;
  }

  return CONTINUITY_STATE.UNKNOWN;
}

function selectAssistantSummary(theme, continuityState, calmState) {
  if (calmState) return null;
  const themeMap = ASSISTANT_SUMMARIES[theme];
  if (!themeMap) return null;
  return (
    themeMap[continuityState] ??
    themeMap._default          ??
    null
  );
}

function computeConfidence(narrative, memory) {
  const nc = narrative?.confidence           ?? 0;
  const mc = memory?.momentum?.confidence    ?? 0;
  return Math.round(((nc + mc) / 2) * 100) / 100;
}

function computeAttentionFromUrgency(urgency, calmState) {
  if (calmState)                          return ATTENTION_LEVELS.CALM;
  if (urgency === URGENCY_LEVEL.IMMEDIATE) return ATTENTION_LEVELS.CRITICAL;
  if (urgency === URGENCY_LEVEL.SOON)      return ATTENTION_LEVELS.URGENT;
  if (urgency === URGENCY_LEVEL.MONITOR)   return ATTENTION_LEVELS.ACTIONABLE;
  return ATTENTION_LEVELS.INFORMATIONAL;
}

/* ============================= */
/* 1. NORMALIZE SEMANTIC SIGNALS */
/* Unified array from all engines*/
/* ============================= */

function normalizeSemanticSignals({ risk, workflows, execution, narrative, memory }) {
  const signals = [];

  // Risk intelligence — ground-level factual signals
  for (const s of risk?.signals ?? []) {
    signals.push({
      type:       s.type,
      severity:   s.severity,
      confidence: 0.9,
      source:     "intelligence",
    });
  }

  // Workflows — lifecycle pressure signals
  for (const w of workflows ?? []) {
    if (
      w.urgency === WORKFLOW_URGENCY.critical ||
      w.urgency === WORKFLOW_URGENCY.high
    ) {
      signals.push({
        type:       `workflow:${w.type}`,
        severity:   w.urgency === WORKFLOW_URGENCY.critical ? "critical" : "high",
        confidence: w.continuityScore ?? 0.5,
        source:     "workflows",
      });
    }
  }

  // Execution — follow-through quality signal
  if (execution?.state) {
    const EXEC_SEVERITY = {
      [EXECUTION_STATE.OVERDUE]:          "critical",
      [EXECUTION_STATE.STALLED]:          "high",
      [EXECUTION_STATE.FRAGMENTED]:       "medium",
      [EXECUTION_STATE.RECOVERING]:       "medium",
      [EXECUTION_STATE.IN_PROGRESS]:      "low",
      [EXECUTION_STATE.FOLLOWING_THROUGH]:"low",
      [EXECUTION_STATE.INITIATED]:        "low",
      [EXECUTION_STATE.SUSTAINED]:        "positive",
      [EXECUTION_STATE.NOT_STARTED]:      "medium",
    };
    signals.push({
      type:       `execution:${execution.state}`,
      severity:   EXEC_SEVERITY[execution.state] ?? "low",
      confidence: 0.8,
      source:     "execution",
    });
  }

  // Narrative — orchestrated dominant theme signal
  if (narrative?.dominantTheme && !narrative?.calmState) {
    const THEME_SEVERITY = {
      [NARRATIVE_THEME.CRISIS_CLUSTER]:       "critical",
      [NARRATIVE_THEME.CONTINUITY_BREAKDOWN]: "high",
      [NARRATIVE_THEME.SPIRITUAL_FATIGUE]:    "high",
      [NARRATIVE_THEME.PRAYER_BURDEN]:        "high",
      [NARRATIVE_THEME.FOLLOWUP_REQUIRED]:    "medium",
      [NARRATIVE_THEME.PASTORAL_OVERLOAD]:    "high",
      [NARRATIVE_THEME.RECOVERY_MOMENTUM]:    "medium",
      [NARRATIVE_THEME.GROWTH_WINDOW]:        "low",
      [NARRATIVE_THEME.HEALTHY_STABILITY]:    "positive",
    };
    signals.push({
      type:       `narrative:${narrative.dominantTheme}`,
      severity:   THEME_SEVERITY[narrative.dominantTheme] ?? "medium",
      confidence: narrative.confidence ?? 0.5,
      source:     "narrative",
    });
  }

  // Memory — longitudinal pattern signals
  for (const p of memory?.patterns ?? []) {
    signals.push({
      type:       `pattern:${p.type}`,
      severity:   p.severity,
      confidence: p.confidence,
      source:     "memory",
    });
  }

  return signals;
}

/* ============================= */
/* 2. PRIMARY ACTION ELECTION    */
/* ONE action from all engines   */
/* ============================= */

function buildPrimaryAction({ narrative, priorities, workflows, suppressedSignals }) {
  // Narrative primary action is the highest-fidelity source
  if (narrative?.primaryAction) return narrative.primaryAction;

  // First unsuppressed priority
  for (const p of priorities ?? []) {
    if (!suppressedSignals?.has(p.key)) return p.message;
  }

  // Most urgent active workflow's suggested action
  const URGENCY_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };
  const topWorkflow = (workflows ?? [])
    .filter(w => w.stage !== WORKFLOW_STAGES.DORMANT && w.stage !== WORKFLOW_STAGES.SUSTAINED)
    .sort((a, b) => (URGENCY_ORDER[a.urgency] ?? 4) - (URGENCY_ORDER[b.urgency] ?? 4))[0];

  return topWorkflow?.nextSuggestedAction ?? null;
}

/* ============================= */
/* 3. SECONDARY ACTIONS          */
/* Up to 2, no duplication       */
/* ============================= */

function buildSecondaryActions({ narrative, priorities, primaryAction }) {
  const suppressedSignals = narrative?.suppressedSignals ?? new Set();
  const used = new Set(primaryAction ? [primaryAction] : []);

  return (priorities ?? [])
    .filter(p => !suppressedSignals.has(p.key))
    .map(p => p.message)
    .filter(msg => !used.has(msg))
    .slice(0, 2);
}

/* ============================= */
/* 4. GROUPED CONTEXT            */
/* Pressure levels per dimension */
/* ============================= */

function buildGroupedContext({ urgentPrayers = 0, activePrayers = 0, execution, workflows = [], memory }) {
  const prayerPressure =
    urgentPrayers > 0    ? "critical"
    : activePrayers > 2  ? "high"
    : activePrayers > 0  ? "low"
    : "none";

  const execState = execution?.state;
  const executionPressure =
    execState === EXECUTION_STATE.OVERDUE   ? "high"
    : execState === EXECUTION_STATE.STALLED ? "high"
    : execState === EXECUTION_STATE.FRAGMENTED || execState === EXECUTION_STATE.INITIATED ? "medium"
    : execState === EXECUTION_STATE.NOT_STARTED ? "none"
    : "low";

  const criticalWfs = (workflows ?? []).filter(
    w => w.urgency === WORKFLOW_URGENCY.critical || w.urgency === WORKFLOW_URGENCY.high
  ).length;
  const workflowPressure =
    criticalWfs > 1 ? "high"
    : criticalWfs === 1 ? "medium"
    : (workflows ?? []).some(w => w.urgency === WORKFLOW_URGENCY.medium) ? "low"
    : "none";

  const trend = memory?.momentum?.trend ?? TREND_TYPES.UNKNOWN;
  const continuityPressure =
    trend === TREND_TYPES.STABLE_NEGLECTED ? "critical"
    : trend === TREND_TYPES.DECLINING ? "medium"
    : trend === TREND_TYPES.RECOVERING || trend === TREND_TYPES.IMPROVING ? "low"
    : trend === TREND_TYPES.STABLE_HEALTHY ? "none"
    : "none";

  return { prayerPressure, executionPressure, workflowPressure, continuityPressure };
}

/* ============================= */
/* 5. SUPPRESSION MAP            */
/* What stays silent             */
/* JSON-safe: arrays not Sets    */
/* ============================= */

function buildSuppressionMap({ narrative, execution }) {
  const calmState = narrative?.calmState ?? false;
  const suppressedSignals = narrative?.suppressedSignals ?? new Set();

  return {
    alerts:         calmState,
    priorityKeys:   [...suppressedSignals],
    executionStrip: calmState || execution?.state === EXECUTION_STATE.NOT_STARTED || execution?.state === EXECUTION_STATE.SUSTAINED,
    narrativeStrip: calmState || !narrative?.summary,
  };
}

/* ============================= */
/* ENTITY ENTRY POINT            */
/* buildPastoralState            */
/* ============================= */

/**
 * Canonical pastoral state for a single entity (person or family).
 * Consolidates all engine outputs into one serializable state object.
 *
 * @param {{
 *   entityType: "person"|"family",
 *   entity:     object|null,
 *   visits:     object[],
 *   prayers:    object[],
 *   workflows:  object[],
 *   memory:     object|null,
 *   narrative:  object|null,
 *   execution:  object|null,
 *   risk:       object|null,
 * }}
 * @returns {PastoralStateObject}
 */
export function buildPastoralState({
  entityType  = "person",
  entity      = null,
  visits      = [],
  prayers     = [],
  workflows   = [],
  memory      = null,
  narrative   = null,
  execution   = null,
  risk        = null,
} = {}) {
  const calmState      = narrative?.calmState ?? false;
  const dominantTheme  = narrative?.dominantTheme ?? null;
  const urgency        = THEME_URGENCY[dominantTheme] ?? (calmState ? URGENCY_LEVEL.NONE : URGENCY_LEVEL.MONITOR);
  const continuityState = deriveContinuityState(memory, execution);

  // Risk level: prefer explicit risk.level, fall back to theme derivation
  const riskLevel = risk?.level ?? THEME_TO_RISK[dominantTheme] ?? RISK_LEVEL.healthy;

  // Most urgent active workflow
  const URGENCY_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };
  const topWorkflow = (workflows ?? [])
    .filter(w => w.stage !== WORKFLOW_STAGES.DORMANT && w.stage !== WORKFLOW_STAGES.SUSTAINED)
    .sort((a, b) => (URGENCY_ORDER[a.urgency] ?? 4) - (URGENCY_ORDER[b.urgency] ?? 4))[0] ?? null;

  const suppressedSignals = narrative?.suppressedSignals ?? new Set();
  const priorities = risk?.priorities ?? [];

  const primaryAction    = buildPrimaryAction({ narrative, priorities, workflows, suppressedSignals });
  const secondaryActions = buildSecondaryActions({ narrative, priorities, primaryAction });
  const groupedContext   = buildGroupedContext({
    urgentPrayers: prayers.filter(p => p.priority === "alta"  && p.status === "activa").length,
    activePrayers: prayers.filter(p => p.status === "activa").length,
    execution,
    workflows,
    memory,
  });
  const semanticSignals  = normalizeSemanticSignals({ risk, workflows, execution, narrative, memory });
  const suppressions     = buildSuppressionMap({ narrative, execution });
  const assistantSummary = selectAssistantSummary(dominantTheme, continuityState, calmState);
  const confidence       = computeConfidence(narrative, memory);
  const attentionLevel   = computeAttentionFromUrgency(urgency, calmState);

  return {
    entityType,
    entityId: entity?.id ?? null,

    dominantTheme,
    urgency,
    continuityState,

    narrativeSummary: narrative?.summary      ?? null,
    assistantSummary,

    primaryAction,
    secondaryActions,

    executionHealth: execution?.state  ?? null,
    momentum:        memory?.momentum?.trend ?? TREND_TYPES.UNKNOWN,
    cadence:         execution?.cadence       ?? null,

    workflowStage:   topWorkflow?.stage   ?? null,
    workflowUrgency: topWorkflow?.urgency ?? null,

    riskLevel,
    attentionLevel,

    groupedContext,

    semanticSignals,

    suppressions,

    confidence,

    updatedAt: getLocalDateString(),
  };
}

/* ============================= */
/* DISTRICT ENTRY POINT          */
/* buildDistrictPastoralState    */
/* ============================= */

/**
 * Canonical pastoral state for the district.
 * Uses proxy data (lastVisitDays) — no N+1 fetches.
 *
 * @param {{
 *   districtWorkflows: object[],
 *   districtNarrative: object|null,
 *   districtExecution: object|null,
 *   load:              object|null,
 *   schedule:          object[],
 *   signals:           object[],
 * }}
 * @returns {DistrictPastoralStateObject}
 */
export function buildDistrictPastoralState({
  districtWorkflows = [],
  districtNarrative = null,
  districtExecution = null,
  load              = null,
  schedule          = [],
  signals           = [],
} = {}) {
  const calmState = districtNarrative?.calmState ?? false;
  const theme     = districtNarrative?.dominantTheme ?? null;
  const urgency   = THEME_URGENCY[theme] ?? (calmState ? URGENCY_LEVEL.NONE : URGENCY_LEVEL.MONITOR);

  const loadLevel    = load?.level ?? LOAD_LEVEL.LIGHT;
  const isOverloaded = loadLevel === LOAD_LEVEL.HEAVY || loadLevel === LOAD_LEVEL.OVERLOADED;

  // Continuity ratio from execution aggregate
  const recentCount    = districtExecution?.recentCount  ?? 0;
  const stalledCount   = districtExecution?.stalledCount ?? 0;
  const overdueCount   = districtExecution?.overdueCount ?? 0;
  const totalChurches  = (recentCount + stalledCount + overdueCount) || 1;
  const neglectedRatio = overdueCount / totalChurches;

  const continuityState =
    neglectedRatio > 0.5  ? CONTINUITY_STATE.NEGLECTED
    : neglectedRatio > 0.25 ? CONTINUITY_STATE.DECLINING
    : recentCount / totalChurches > 0.6 ? CONTINUITY_STATE.ACTIVE
    : CONTINUITY_STATE.UNKNOWN;

  // District urgency may escalate from load
  const effectiveUrgency = isOverloaded && urgency !== URGENCY_LEVEL.IMMEDIATE
    ? URGENCY_LEVEL.SOON
    : urgency;

  const assistantSummary = calmState ? null : (DISTRICT_SUMMARIES[theme] ?? null);

  // Scheduled church count (non-zero schedule = active planning)
  const scheduledCount = (schedule ?? []).length;

  // Most urgent district workflow
  const URGENCY_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };
  const topWorkflow = (districtWorkflows ?? [])
    .filter(w => w.stage !== WORKFLOW_STAGES.DORMANT && w.stage !== WORKFLOW_STAGES.SUSTAINED)
    .sort((a, b) => (URGENCY_ORDER[a.urgency] ?? 4) - (URGENCY_ORDER[b.urgency] ?? 4))[0] ?? null;

  const prayerSignals = (signals ?? []).filter(
    s => s.type === "URGENT_PRAYER" || s.type === "ACTIVE_PRAYERS"
  );

  const criticalWorkflows = (districtWorkflows ?? []).filter(
    w => w.urgency === WORKFLOW_URGENCY.critical || w.urgency === WORKFLOW_URGENCY.high
  ).length;

  const semanticSignals = [
    ...(isOverloaded
      ? [{ type: "district:overload",            severity: "high",   confidence: 0.9, source: "scheduling" }]
      : []),
    ...(neglectedRatio > 0.3
      ? [{ type: "district:continuity_collapse", severity: "high",   confidence: 0.8, source: "execution"  }]
      : []),
    ...(neglectedRatio <= 0.1 && recentCount > 0
      ? [{ type: "district:healthy_rhythm",       severity: "positive", confidence: 0.8, source: "execution" }]
      : []),
    ...prayerSignals.slice(0, 3).map(s => ({ ...s, source: "intelligence" })),
  ];

  const suppressedNarrative = districtNarrative?.suppressedSignals ?? new Set();

  return {
    entityType: "district",
    entityId:   null,

    dominantTheme:  theme,
    urgency:        effectiveUrgency,
    continuityState,

    narrativeSummary: districtNarrative?.headline ?? null,
    assistantSummary,

    primaryAction:   districtNarrative?.context    ?? topWorkflow?.nextSuggestedAction ?? null,
    secondaryActions: [],

    executionHealth:  null,
    executionSummary: districtExecution?.summary ?? null,

    loadLevel,
    hasOverload: isOverloaded,
    scheduledCount,

    workflowStage:   topWorkflow?.stage   ?? null,
    workflowUrgency: topWorkflow?.urgency ?? null,

    riskLevel: THEME_TO_RISK[theme] ?? RISK_LEVEL.healthy,
    attentionLevel: computeAttentionFromUrgency(effectiveUrgency, calmState),

    groupedContext: {
      prayerPressure:     prayerSignals.length > 0 ? "high" : "none",
      executionPressure:  neglectedRatio > 0.4 ? "high" : neglectedRatio > 0.2 ? "medium" : "low",
      workflowPressure:   criticalWorkflows > 2 ? "high" : criticalWorkflows > 0 ? "medium" : "none",
      continuityPressure: continuityState === CONTINUITY_STATE.NEGLECTED  ? "critical"
                        : continuityState === CONTINUITY_STATE.DECLINING  ? "medium"
                        : continuityState === CONTINUITY_STATE.ACTIVE     ? "none"
                        : "none",
    },

    semanticSignals,

    suppressions: {
      alerts:       calmState,
      priorityKeys: [...suppressedNarrative],
    },

    confidence: districtNarrative?.confidence ?? 0.5,

    updatedAt: getLocalDateString(),
  };
}
